import { useAppSelector } from '@/app/hooks'
import { CloseButton } from '@/components/atoms/close-button'
import { LoadingFull } from '@/components/atoms/loading-full'
import { Header } from '@/components/molecules/header'
import { CUSTOM_BLOCK, CUSTOM_BUTTON, CUSTOM_LINK } from '@/components/template/custom-design-style'
import { css } from '@emotion/react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export type IdentityVerifyCameraGuide = {
  aspectRatio: string
  widthPercent: number
}

export type IdentityVerifyCameraShutter =
  | { variant: 'button'; label: string }
  | { variant: 'link'; label: string; hint: React.ReactNode; illustrationUrl: string }

type Props = {
  cameraInstruction: string
  guide: IdentityVerifyCameraGuide
  guideImageUrl?: string
  shutter: IdentityVerifyCameraShutter
  autoDetect?: (canvas: HTMLCanvasElement) => Promise<boolean>
  onCapture: (blob: Blob, isManual: boolean) => void | Promise<void>
  onCancel: () => void
}

const AutoDetectIntervalMs = 2000

export const IdentityVerifyCamera: React.FC<Props> = ({
  cameraInstruction,
  guide,
  guideImageUrl,
  shutter,
  autoDetect,
  onCapture,
  onCancel,
}) => {
  const { t } = useTranslation()
  const hotelName = useAppSelector(state => state.hotelGuide.facilityBasicInfo.hotelName)

  const videoContainerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const guideRef = useRef<HTMLDivElement>(null)

  const [isCameraReady, setIsCameraReady] = useState(false)
  const [cameraError, setCameraError] = useState<string>()
  const [isCapturing, setIsCapturing] = useState(false)
  // setState の反映を待たずに連打を弾くための同期フラグ
  const isCapturingRef = useRef(false)
  const isDetectingRef = useRef(false)
  // 撮影が走った回数。自動判定は 2 秒かかるため、待っている間に手動撮影が入り得る
  const captureCountRef = useRef(0)
  // インターバルは isCameraReady が変わるまで張り直さないため、クロージャで onCapture を
  // 直接参照すると古い props を呼びかねない。ref 経由で常に最新を呼ぶ
  const onCaptureRef = useRef(onCapture)

  useEffect(() => {
    onCaptureRef.current = onCapture
  }, [onCapture])

  // video は object-fit: cover で拡大表示されるため、表示座標をそのまま実解像度の座標として
  // 使うと枠とずれる。表示→実解像度の倍率で換算してから切り出す
  const captureGuideRegion = (): HTMLCanvasElement | undefined => {
    const video = videoRef.current
    const container = videoContainerRef.current
    const guideEl = guideRef.current
    if (!video || !container || !guideEl || video.videoWidth <= 0 || video.videoHeight <= 0) {
      return undefined
    }

    const containerRect = container.getBoundingClientRect()
    const guideRect = guideEl.getBoundingClientRect()

    // cover の拡大率（ネイティブ→表示）は大きい方の比率なので、逆変換は Math.min。
    // Math.max にすると、動画とコンテナのアスペクト比が違うたびに切り出し範囲がずれる
    const coverScale = Math.min(video.videoWidth / containerRect.width, video.videoHeight / containerRect.height)
    const renderedWidth = containerRect.width * coverScale
    const renderedHeight = containerRect.height * coverScale
    const offsetX = (renderedWidth - video.videoWidth) / 2
    const offsetY = (renderedHeight - video.videoHeight) / 2

    const sourceX = (guideRect.left - containerRect.left) * coverScale - offsetX
    const sourceY = (guideRect.top - containerRect.top) * coverScale - offsetY
    const sourceWidth = guideRect.width * coverScale
    const sourceHeight = guideRect.height * coverScale

    const fullFrame = document.createElement('canvas')
    fullFrame.width = video.videoWidth
    fullFrame.height = video.videoHeight
    const fullFrameCtx = fullFrame.getContext('2d')
    if (!fullFrameCtx) {
      return undefined
    }
    fullFrameCtx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight)

    // 左/上のはみ出し分は幅・高さからも差し引く。切り捨てるだけだと縦横比が崩れる
    const clampedX = Math.max(0, sourceX)
    const clampedY = Math.max(0, sourceY)
    const widthAfterLeftClip = sourceWidth - (clampedX - sourceX)
    const heightAfterTopClip = sourceHeight - (clampedY - sourceY)
    const clampedWidth = Math.min(widthAfterLeftClip, video.videoWidth - clampedX)
    const clampedHeight = Math.min(heightAfterTopClip, video.videoHeight - clampedY)
    if (clampedWidth <= 0 || clampedHeight <= 0) {
      return undefined
    }

    const cropped = document.createElement('canvas')
    cropped.width = clampedWidth
    cropped.height = clampedHeight
    const croppedCtx = cropped.getContext('2d')
    if (!croppedCtx) {
      return undefined
    }
    croppedCtx.drawImage(fullFrame, clampedX, clampedY, clampedWidth, clampedHeight, 0, 0, clampedWidth, clampedHeight)
    return cropped
  }

  const shutterToBlob = (canvas: HTMLCanvasElement): Promise<Blob | undefined> =>
    new Promise(resolve => canvas.toBlob(blob => resolve(blob ?? undefined), 'image/jpeg', 0.92))

  const handleShutter = async (isManual: boolean) => {
    if (isCapturingRef.current || !isCameraReady) {
      return
    }
    isCapturingRef.current = true
    captureCountRef.current += 1
    setIsCapturing(true)
    try {
      const region = captureGuideRegion()
      if (!region) {
        return
      }
      const blob = await shutterToBlob(region)
      if (blob) {
        await onCaptureRef.current(blob, isManual)
      }
    } finally {
      isCapturingRef.current = false
      setIsCapturing(false)
    }
  }

  useEffect(() => {
    if (!autoDetect || !isCameraReady) {
      return
    }
    const interval = setInterval(async () => {
      if (isCapturingRef.current || isDetectingRef.current) {
        return
      }
      const region = captureGuideRegion()
      if (!region) {
        return
      }
      isDetectingRef.current = true
      const captureCountBeforeDetect = captureCountRef.current
      try {
        const detected = await autoDetect(region)
        // 判定を待っている間に手動で撮っていたら、その結果は使わない（同じ枠を二重に送ってしまう）
        if (detected && captureCountRef.current === captureCountBeforeDetect) {
          await handleShutter(false)
        }
      } catch {
        // 自動判定の例外は黙って次の回に任せる（失敗しても手動撮影に落ちる設計）
      } finally {
        isDetectingRef.current = false
      }
    }, AutoDetectIntervalMs)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDetect, isCameraReady])

  useEffect(() => {
    let cancelled = false
    let stream: MediaStream | undefined

    const start = async () => {
      try {
        // facingMode を exact にすると背面カメラが無い端末（PC 等）で開けなくなる。
        // 解像度は ideal 指定が無いとブラウザ既定の 640×480 になり、ガイド枠内が実解像度で
        // 小さくなりすぎて OCR が読めなくなる。min にすると対応しないカメラで開けなくなる
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
        })
        if (cancelled) {
          stream.getTracks().forEach(track => track.stop())
          return
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }
      } catch {
        if (!cancelled) {
          setCameraError(t('Failed to start the camera. Please allow camera access.'))
        }
      }
    }
    start()

    return () => {
      cancelled = true
      stream?.getTracks().forEach(track => track.stop())
    }
  }, [])

  const isProcessing = isCapturing

  const guideSizeStyle = useMemo(
    () =>
      css({
        width: `${guide.widthPercent}%`,
        aspectRatio: guide.aspectRatio,
      }),
    [guide.widthPercent, guide.aspectRatio],
  )

  return (
    <div css={overlayStyle}>
      <div css={headerWrapperStyle}>
        <Header hotelName={hotelName} right={<CloseButton onClick={onCancel} customStyle={closeButtonStyle} />} />
      </div>

      <div css={videoWrapperStyle} ref={videoContainerRef}>
        <video ref={videoRef} css={videoStyle} playsInline muted autoPlay onCanPlay={() => setIsCameraReady(true)} />

        <div css={guideAreaStyle}>
          <p css={instructionStyle}>{cameraInstruction}</p>
          <div css={[guideFrameStyle, guideSizeStyle]} ref={guideRef}>
            {guideImageUrl && <img src={guideImageUrl} css={guideImageStyle} alt="" />}
          </div>
        </div>

        {cameraError && <p css={cameraErrorTextStyle}>{cameraError}</p>}
      </div>

      <div css={[footerStyle, shutter.variant === 'link' && footerLinkStyle]} className={CUSTOM_BLOCK}>
        {shutter.variant === 'button' ? (
          <button
            type="button"
            css={shutterButtonStyle}
            className={CUSTOM_BUTTON}
            disabled={!isCameraReady || isProcessing}
            onClick={() => handleShutter(true)}
          >
            {shutter.label}
          </button>
        ) : (
          <>
            <div css={autoShutterTextColumnStyle}>
              <p css={autoShutterHintStyle}>{shutter.hint}</p>
              <button
                type="button"
                css={manualLinkButtonStyle}
                className={CUSTOM_LINK}
                disabled={!isCameraReady || isProcessing}
                onClick={() => handleShutter(true)}
              >
                {shutter.label}
              </button>
            </div>
            <img css={autoShutterIllustrationStyle} src={shutter.illustrationUrl} alt="" />
          </>
        )}
      </div>

      <LoadingFull loading={isProcessing} />
    </div>
  )
}

const overlayStyle = css({
  position: 'fixed',
  inset: 0,
  zIndex: 500,
  display: 'flex',
  flexDirection: 'column',
  background: '#000',
})

const headerWrapperStyle = css({
  flexShrink: 0,
})

const closeButtonStyle = css({
  flexShrink: 0,
  width: '30px',
  height: '30px',
  background: 'none',
  borderRadius: 0,
  '&:before, &:after': {
    width: '3px',
    height: '28px',
  },
})

const videoWrapperStyle = css({
  position: 'relative',
  flexGrow: 1,
  overflow: 'hidden',
  background: '#000',
})

const videoStyle = css({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
})

const guideAreaStyle = css({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '1.5rem',
  padding: '0 1.5rem',
})

const instructionStyle = css({
  color: '#fff',
  fontSize: '14px',
  fontWeight: 'bold',
  textAlign: 'center',
  whiteSpace: 'pre-line',
  lineHeight: 1.5,
})

const guideFrameStyle = css({
  position: 'relative',
  maxWidth: '360px',
  border: '2px solid #fff',
  borderRadius: '15px',
})

const guideImageStyle = css({
  display: 'block',
  width: '100%',
  height: '100%',
})

const cameraErrorTextStyle = css({
  position: 'absolute',
  left: '1.5rem',
  right: '1.5rem',
  bottom: '1rem',
  color: '#fff',
  fontSize: '12px',
  textAlign: 'center',
})

const footerStyle = css({
  flexShrink: 0,
  padding: '1rem 1.5rem 1.5rem',
})

const footerLinkStyle = css({
  position: 'relative',
  height: '158px',
  paddingTop: '24px',
})

const shutterButtonStyle = css({
  width: '100%',
  height: '48px',
  fontSize: '16px',
  cursor: 'pointer',
  '&:disabled': {
    opacity: 0.3,
  },
})

const autoShutterTextColumnStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  // 159px の挿絵は footer 右端に絶対配置。footer 左パディング(24px)を差し引いた分だけ
  // 手前で折り返さないと、footer の実描画幅が変わった時に挿絵と重なる
  maxWidth: 'calc(100% - 135px)',
})

const autoShutterHintStyle = css({
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '19px',
  letterSpacing: '0.7px',
})

const autoShutterIllustrationStyle = css({
  position: 'absolute',
  right: 0,
  bottom: 0,
  width: '159px',
  height: '159px',
  objectFit: 'contain',
})

const manualLinkButtonStyle = css({
  border: 'none',
  background: 'none',
  padding: 0,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '19px',
  letterSpacing: '0.7px',
  cursor: 'pointer',
  '&:disabled': {
    opacity: 0.3,
  },
  textAlign: 'left',
})
